import { MarkdownContent } from './markdown-content';
import { cn } from '@/lib/cn';

export interface MarkdownInlineDiffProps {
  oldContent: string;
  newContent: string;
  size?: 'xs' | 'sm';
  className?: string;
  showGutter?: boolean;
  onAccept?: () => void;
  onReject?: () => void;
}

type DiffOp =
  | { kind: 'equal'; text: string }
  | { kind: 'remove'; text: string }
  | { kind: 'add'; text: string };

// Split markdown on blank-line boundaries so each block (heading, paragraph,
// list, fence) is a single diff unit. Fenced code spanning blanks would be
// torn — acceptable v1 trade-off; keeps the algorithm simple.
function splitBlocks(md: string): string[] {
  return md
    .split(/\n{2,}/)
    .map((b) => b.replace(/^\n+|\n+$/g, ''))
    .filter((b) => b.length > 0);
}

function diffBlocks(oldBlocks: string[], newBlocks: string[]): DiffOp[] {
  const m = oldBlocks.length;
  const n = newBlocks.length;
  // LCS table on trimmed equality so trailing-whitespace changes don't
  // surface as full-block diffs.
  const oldTrim = oldBlocks.map((b) => b.trim());
  const newTrim = newBlocks.map((b) => b.trim());
  const dp: number[][] = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = m - 1; i >= 0; i--) {
    for (let j = n - 1; j >= 0; j--) {
      dp[i][j] = oldTrim[i] === newTrim[j]
        ? dp[i + 1][j + 1] + 1
        : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }
  const ops: DiffOp[] = [];
  let i = 0;
  let j = 0;
  while (i < m && j < n) {
    if (oldTrim[i] === newTrim[j]) {
      ops.push({ kind: 'equal', text: oldBlocks[i] });
      i++;
      j++;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      ops.push({ kind: 'remove', text: oldBlocks[i] });
      i++;
    } else {
      ops.push({ kind: 'add', text: newBlocks[j] });
      j++;
    }
  }
  while (i < m) ops.push({ kind: 'remove', text: oldBlocks[i++] });
  while (j < n) ops.push({ kind: 'add', text: newBlocks[j++] });
  return ops;
}

function GutterCell({ marker, show }: { marker: string; show: boolean }) {
  if (!show) return null;
  return (
    <div
      aria-hidden
      className="select-none w-6 shrink-0 px-2 pt-2 font-mono text-xs text-muted-foreground"
    >
      {marker}
    </div>
  );
}

export function MarkdownInlineDiff({
  oldContent,
  newContent,
  size = 'sm',
  className,
  showGutter = true,
  onAccept,
  onReject,
}: MarkdownInlineDiffProps) {
  // Identical content guard — keeps the component safe for callers that
  // don't pre-check (the production path already checks, but protect anyway).
  if (oldContent === newContent) {
    return (
      <div className={cn('rounded border border-border bg-card p-3', className)}>
        <MarkdownContent size={size}>{oldContent}</MarkdownContent>
      </div>
    );
  }

  const ops = diffBlocks(splitBlocks(oldContent), splitBlocks(newContent));
  const hasActions = !!(onAccept || onReject);

  return (
    <div className={cn('rounded border border-border bg-card', className)}>
      {hasActions && (
        <div className="flex items-center justify-end gap-2 border-b border-border px-3 py-2">
          {onReject && (
            <button
              type="button"
              onClick={onReject}
              className="focus-ring rounded border border-border px-2 py-1 text-xs text-foreground hover:bg-accent"
            >
              Reject
            </button>
          )}
          {onAccept && (
            <button
              type="button"
              onClick={onAccept}
              className="focus-ring rounded border border-status-success-border bg-status-success-bg px-2 py-1 text-xs text-status-success-fg hover:opacity-90"
            >
              Accept
            </button>
          )}
        </div>
      )}
      <div className="divide-y divide-border">
        {ops.map((op, idx) => {
          const key = `${op.kind}-${idx}`;
          if (op.kind === 'equal') {
            return (
              <div key={key} className="flex">
                <GutterCell marker=" " show={showGutter} />
                <div className="flex-1 px-3 py-2">
                  <MarkdownContent size={size}>{op.text}</MarkdownContent>
                </div>
              </div>
            );
          }
          if (op.kind === 'remove') {
            return (
              <div
                key={key}
                className="flex bg-status-error-bg border-l-2 border-status-error-border"
              >
                <GutterCell marker="−" show={showGutter} />
                <div className="flex-1 px-3 py-2 [&_*]:line-through [&_*]:decoration-status-error-fg/60">
                  <MarkdownContent size={size}>{op.text}</MarkdownContent>
                </div>
              </div>
            );
          }
          return (
            <div
              key={key}
              className="flex bg-status-success-bg border-l-2 border-status-success-border"
            >
              <GutterCell marker="+" show={showGutter} />
              <div className="flex-1 px-3 py-2">
                <MarkdownContent size={size}>{op.text}</MarkdownContent>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
